<template>
  <div class="telegram-workspace">
    <div class="d-flex flex-wrap align-center ga-3 mb-4">
      <h2 class="telegram-workspace__title">
        {{ $t("ncrm.integrations.telegram.title") }}
      </h2>
      <span class="telegram-workspace__subtitle">
        {{ $t("ncrm.integrations.telegram.subtitle") }}
      </span>
      <v-spacer />
      <v-btn color="primary" @click="openCreateDialog">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.integrations.telegram.addAccount") }}
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="accounts"
      :loading="tableLoading"
      density="comfortable"
      class="elevation-0 telegram-accounts-table"
      item-key="id"
      :row-props="getRowProps"
      @click:row="onRowClick"
    >
      <template #item.status="{ item }">
        <v-chip
          :color="item.status === 'active' ? 'success' : 'neutral'"
        >
          {{ $t(`ncrm.integrations.telegram.status.${item.status}`) }}
        </v-chip>
      </template>

      <template #item.bot_username="{ item }">
        <span v-if="item.bot_username" class="font-weight-medium">@{{ item.bot_username }}</span>
        <span v-else class="text-medium-emphasis">—</span>
      </template>

      <template #item.last_test_status="{ item }">
        <v-chip :color="testStatusColor(item.last_test_status)">
          {{ $t(`ncrm.integrations.telegram.lastTest.${item.last_test_status}`) }}
        </v-chip>
        <span
          v-if="item.last_tested_at"
          class="ml-2 text-caption text-medium-emphasis"
        >
          {{ formatDate(item.last_tested_at) }}
        </span>
      </template>

      <template #item.actions="{ item }">
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          :loading="testingId === item.id"
          @click="handleTest(item)"
        >
          <v-icon start size="16">mdi-lan-connect</v-icon>
          {{ $t("ncrm.integrations.telegram.actions.test") }}
        </v-btn>
        <v-btn size="small" variant="text" class="ml-1" @click="openEditDialog(item)">
          <v-icon size="18">mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          size="small"
          variant="text"
          color="error"
          class="ml-1"
          @click="openDeleteDialog(item)"
        >
          <v-icon size="18">mdi-delete-outline</v-icon>
        </v-btn>
      </template>

      <template #no-data>
        <v-alert type="info" class="ma-4">
          {{ $t("ncrm.integrations.telegram.empty") }}
        </v-alert>
      </template>
    </v-data-table>

    <TelegramWebhookCard
      v-if="selectedAccount"
      :account="selectedAccount"
      @account-updated="onAccountUpdated"
      @notify="onChildNotify"
    />

    <TelegramDigestCard
      v-if="selectedAccount"
      :account="selectedAccount"
      @account-updated="onAccountUpdated"
      @notify="onChildNotify"
    />

    <TelegramTemplatesPanel @notify="onChildNotify" />

    <!-- Create / Edit dialog -->
    <v-dialog v-model="formDialog.show" max-width="560" persistent>
      <v-card>
        <v-card-title>
          {{
            formDialog.mode === "create"
              ? $t("ncrm.integrations.telegram.dialog.create.title")
              : $t("ncrm.integrations.telegram.dialog.edit.title")
          }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-text-field
            v-model="formDialog.data.name"
            :label="$t('ncrm.integrations.telegram.dialog.fields.name')"
            :rules="[required]"
            required
          />
          <v-textarea
            v-model="formDialog.data.description"
            :label="$t('ncrm.integrations.telegram.dialog.fields.description')"
            rows="2"
            auto-grow
          />
          <v-select
            v-if="formDialog.mode === 'edit'"
            v-model="formDialog.data.status"
            :items="statusOptions"
            :label="$t('ncrm.integrations.telegram.dialog.fields.status')"
          />
          <v-checkbox
            v-if="formDialog.mode === 'edit'"
            v-model="formDialog.editToken"
            :label="$t('ncrm.integrations.telegram.dialog.editKeys')"
            density="comfortable"
            hide-details
            class="mb-2"
          />
          <template v-if="formDialog.mode === 'create' || formDialog.editToken">
            <v-text-field
              v-model="formDialog.data.botToken"
              :label="$t('ncrm.integrations.telegram.dialog.fields.botToken')"
              :hint="$t('ncrm.integrations.telegram.dialog.fields.botTokenHint')"
              persistent-hint
              type="password"
              :rules="[required]"
              required
              autocomplete="off"
            />
          </template>
          <v-alert v-if="formDialog.error" type="error" class="mt-3">
            {{ formDialog.error }}
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="formDialog.saving" @click="closeFormDialog">
            {{ $t("ncrm.integrations.telegram.dialog.actions.cancel") }}
          </v-btn>
          <v-btn color="primary" :loading="formDialog.saving" @click="submitForm">
            {{ $t("ncrm.integrations.telegram.dialog.actions.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete dialog with DELETE confirmation typing -->
    <v-dialog v-model="deleteDialog.show" max-width="520" persistent>
      <v-card>
        <v-card-title>
          {{ $t("ncrm.integrations.telegram.dialog.delete.title") }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="mb-3">
            {{
              $t("ncrm.integrations.telegram.dialog.delete.confirmText", {
                name: deleteDialog.account?.name || "",
              })
            }}
          </p>
          <p class="text-caption text-medium-emphasis mb-3">
            {{ $t("ncrm.integrations.telegram.dialog.delete.confirmHint") }}
          </p>
          <v-text-field
            v-model="deleteDialog.confirmation"
            :label="$t('ncrm.integrations.telegram.dialog.delete.confirmType')"
            autocomplete="off"
          />
          <v-alert v-if="deleteDialog.error" type="error" class="mt-2">
            {{ deleteDialog.error }}
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="deleteDialog.deleting" @click="closeDeleteDialog">
            {{ $t("ncrm.integrations.telegram.dialog.actions.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            :loading="deleteDialog.deleting"
            :disabled="deleteDialog.confirmation !== 'DELETE'"
            @click="confirmDelete"
          >
            {{ $t("ncrm.integrations.telegram.dialog.actions.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="3500" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";
import TelegramWebhookCard from "@/components/integrations/telegram/TelegramWebhookCard.vue";
import TelegramDigestCard from "@/components/integrations/telegram/TelegramDigestCard.vue";
import TelegramTemplatesPanel from "@/components/integrations/telegram/TelegramTemplatesPanel.vue";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

const buildEmptyForm = () => ({
  id: null,
  name: "",
  description: "",
  botToken: "",
  status: "active",
});

export default {
  name: "TelegramWorkspace",
  components: { TelegramWebhookCard, TelegramDigestCard, TelegramTemplatesPanel },
  data() {
    return {
      accounts: [],
      tableLoading: false,
      testingId: null,
      selectedAccountId: null,
      formDialog: {
        show: false,
        mode: "create",
        editToken: false,
        saving: false,
        error: "",
        data: buildEmptyForm(),
      },
      deleteDialog: {
        show: false,
        account: null,
        confirmation: "",
        deleting: false,
        error: "",
      },
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t("ncrm.integrations.telegram.headers.name"), key: "name" },
        { title: this.$t("ncrm.integrations.telegram.headers.description"), key: "description" },
        { title: this.$t("ncrm.integrations.telegram.headers.status"), key: "status" },
        { title: this.$t("ncrm.integrations.telegram.headers.botUsername"), key: "bot_username" },
        { title: this.$t("ncrm.integrations.telegram.headers.lastTest"), key: "last_test_status" },
        {
          title: this.$t("ncrm.integrations.telegram.headers.actions"),
          key: "actions",
          sortable: false,
          align: "end",
        },
      ];
    },
    statusOptions() {
      return [
        { title: this.$t("ncrm.integrations.telegram.status.active"), value: "active" },
        { title: this.$t("ncrm.integrations.telegram.status.disabled"), value: "disabled" },
      ];
    },
    selectedAccount() {
      if (!this.selectedAccountId) return null;
      return this.accounts.find((a) => a.id === this.selectedAccountId) || null;
    },
  },
  mounted() {
    this.fetchAccounts();
  },
  methods: {
    required(value) {
      return (
        !!String(value || "").trim() ||
        this.$t("ncrm.integrations.telegram.dialog.requiredField")
      );
    },
    formatDate(value) {
      if (!value) return "";
      try {
        return formatDisplayDateTime(value);
      } catch (_) {
        return String(value);
      }
    },
    testStatusColor(status) {
      if (status === "ok") return "success";
      if (status === "error") return "error";
      return "neutral";
    },
    showSnackbar(message, color = "info") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    extractError(error, fallbackKey) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallbackKey)
      );
    },
    async fetchAccounts() {
      this.tableLoading = true;
      try {
        const { data } = await axios.get("/integrations/telegram/accounts");
        this.accounts = Array.isArray(data?.accounts) ? data.accounts : [];
        if (this.accounts.length === 1) {
          this.selectedAccountId = this.accounts[0].id;
        } else if (
          this.selectedAccountId &&
          !this.accounts.some((a) => a.id === this.selectedAccountId)
        ) {
          this.selectedAccountId = null;
        }
      } catch (error) {
        this.showSnackbar(
          this.extractError(error, "ncrm.integrations.telegram.snackbar.loadError"),
          "error"
        );
      } finally {
        this.tableLoading = false;
      }
    },
    getRowProps({ item }) {
      if (!item) return {};
      const isSelected = this.selectedAccountId === item.id;
      return {
        class: isSelected ? "telegram-accounts-row--selected" : "",
        style: { cursor: "pointer" },
      };
    },
    onRowClick(_event, row) {
      const item = row?.item || row;
      if (!item?.id) return;
      this.selectedAccountId = item.id === this.selectedAccountId ? null : item.id;
    },
    onAccountUpdated(updated) {
      if (!updated?.id) return;
      const idx = this.accounts.findIndex((a) => a.id === updated.id);
      if (idx >= 0) {
        this.accounts.splice(idx, 1, { ...this.accounts[idx], ...updated });
      }
    },
    onChildNotify(payload) {
      if (!payload) return;
      this.showSnackbar(payload.message || "", payload.color || "info");
    },
    openCreateDialog() {
      this.formDialog = {
        show: true,
        mode: "create",
        editToken: false,
        saving: false,
        error: "",
        data: buildEmptyForm(),
      };
    },
    openEditDialog(account) {
      this.formDialog = {
        show: true,
        mode: "edit",
        editToken: false,
        saving: false,
        error: "",
        data: {
          id: account.id,
          name: account.name || "",
          description: account.description || "",
          botToken: "",
          status: account.status || "active",
        },
      };
    },
    closeFormDialog() {
      this.formDialog.show = false;
    },
    async submitForm() {
      this.formDialog.error = "";
      const { mode, editToken, data } = this.formDialog;
      if (!String(data.name || "").trim()) {
        this.formDialog.error = this.$t("ncrm.integrations.telegram.dialog.requiredField");
        return;
      }
      if ((mode === "create" || editToken) && !String(data.botToken || "").trim()) {
        this.formDialog.error = this.$t("ncrm.integrations.telegram.dialog.requiredField");
        return;
      }
      this.formDialog.saving = true;
      try {
        if (mode === "create") {
          const payload = {
            name: data.name.trim(),
            description: data.description?.trim() || null,
            botToken: data.botToken.trim(),
          };
          await axios.post("/integrations/telegram/accounts", payload);
          this.showSnackbar(this.$t("ncrm.integrations.telegram.snackbar.created"), "success");
        } else {
          const payload = {
            name: data.name.trim(),
            description: data.description?.trim() || null,
            status: data.status,
          };
          if (editToken) {
            payload.botToken = data.botToken.trim();
          }
          await axios.patch(`/integrations/telegram/accounts/${data.id}`, payload);
          this.showSnackbar(this.$t("ncrm.integrations.telegram.snackbar.updated"), "success");
        }
        this.formDialog.show = false;
        await this.fetchAccounts();
      } catch (error) {
        this.formDialog.error = this.extractError(
          error,
          "ncrm.integrations.telegram.snackbar.saveError"
        );
      } finally {
        this.formDialog.saving = false;
      }
    },
    openDeleteDialog(account) {
      this.deleteDialog = {
        show: true,
        account,
        confirmation: "",
        deleting: false,
        error: "",
      };
    },
    closeDeleteDialog() {
      this.deleteDialog.show = false;
    },
    async confirmDelete() {
      if (this.deleteDialog.confirmation !== "DELETE") return;
      this.deleteDialog.deleting = true;
      this.deleteDialog.error = "";
      try {
        await axios.delete(`/integrations/telegram/accounts/${this.deleteDialog.account.id}`);
        this.showSnackbar(this.$t("ncrm.integrations.telegram.snackbar.deleted"), "success");
        this.deleteDialog.show = false;
        await this.fetchAccounts();
      } catch (error) {
        this.deleteDialog.error = this.extractError(
          error,
          "ncrm.integrations.telegram.snackbar.deleteError"
        );
      } finally {
        this.deleteDialog.deleting = false;
      }
    },
    async handleTest(account) {
      this.testingId = account.id;
      try {
        const { data } = await axios.post(
          `/integrations/telegram/accounts/${account.id}/test`,
          {},
          { validateStatus: () => true }
        );
        if (data?.ok) {
          this.showSnackbar(
            this.$t("ncrm.integrations.telegram.snackbar.testOk", {
              username: data.bot?.username || account.name,
            }),
            "success"
          );
        } else {
          this.showSnackbar(
            data?.error || data?.message || this.$t("ncrm.integrations.telegram.snackbar.testError"),
            "error"
          );
        }
        await this.fetchAccounts();
      } catch (error) {
        this.showSnackbar(
          this.extractError(error, "ncrm.integrations.telegram.snackbar.testError"),
          "error"
        );
      } finally {
        this.testingId = null;
      }
    },
  },
};
</script>

<style scoped>
.telegram-workspace__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-strong);
}
.telegram-workspace__subtitle {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.7;
  padding-left: 8px;
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.18);
}
.telegram-accounts-table :deep(.telegram-accounts-row--selected) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}
</style>
